import { CSSProperties } from 'react';
import { ColumnDirection, LayoutElementStyle } from '@repo/types';

/**
 * Inline styles for the rows and columns a CMS page is laid out with.
 *
 * Rows and columns carry the identical seven background/sizing properties, and the same
 * expression list was written out three times — once for the row's own style, and twice for
 * the column (inside the `fitToSiblings` wrapper and bare). Shared here so they cannot drift.
 *
 * `backgroundImage` is passed in already resolved rather than computed here: a gradient wins
 * over a background image, and the gradient-to-CSS conversion stays next to `Row`, which is
 * the only thing that needs it.
 */
export const buildElementStyle = (
  style: LayoutElementStyle | undefined,
  backgroundImage: string | undefined
): CSSProperties => ({
  backgroundColor: style?.gradient ? undefined : style?.backgroundColor || undefined,
  backgroundImage,
  backgroundSize: style?.backgroundSize || undefined,
  backgroundPosition: style?.backgroundPosition || undefined,
  backgroundRepeat: style?.backgroundRepeat || undefined,
  minHeight: style?.height || undefined,
  maxHeight: style?.maxHeight || undefined,
});

/** Flex placement from a column's alignment/direction, on top of its element style. */
export const buildColumnStyle = (
  style: LayoutElementStyle | undefined,
  backgroundImage: string | undefined,
  direction: ColumnDirection,
  justifyContent: string
): CSSProperties => ({
  flexDirection: direction === ColumnDirection.Bottom ? 'column' : 'row',
  justifyContent,
  alignItems: direction === ColumnDirection.Bottom ? justifyContent : 'center',
  ...buildElementStyle(style, backgroundImage),
});
